import { useEffect } from "react";

interface SeoHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>;
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = "website",
  jsonLd,
}: SeoHeadProps) {
  useEffect(() => {
    const baseUrl = "https://jmtt.example.com";

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        if (name.startsWith("og:") || name.startsWith("twitter:")) {
          el.setAttribute("property", name);
        } else {
          el.setAttribute("name", name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);

    const fullUrl = canonicalPath ? `${baseUrl}${canonicalPath}` : baseUrl;
    const existingCanonical = document.querySelector("link[rel='canonical']");
    if (existingCanonical) {
      existingCanonical.setAttribute("href", fullUrl);
    }

    setMeta("og:type", ogType);
    setMeta("og:title", title);
    setMeta("og:description", description);
    setMeta("og:url", fullUrl);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    if (jsonLd) {
      const existingScript = document.querySelector("script[data-dynamic-ld]");
      if (existingScript) existingScript.remove();

      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-dynamic-ld", "true");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const script = document.querySelector("script[data-dynamic-ld]");
      if (script) script.remove();
    };
  }, [title, description, keywords, canonicalPath, ogType, jsonLd]);

  return null;
}